import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  IconName,
  ModalCloseButton,
  Popover,
  Text,
} from '@usealloy/component-library';
import {
  OptionGroup,
  PopoverContent,
  PopoverFooter,
  StyledBox,
  StyledChip,
} from 'flow/editor/components/NodeEditorDrawer/MatrixModel/AxisConfigPopover/styles';
import { getUpdatedOutputValueRange } from './helpers';
import type { ValueOf } from '~/helpers/typescript';
import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';
import { HighRangeOption, LowRangeOption } from 'flow/service/actions';

interface ValueRangeConfigPopoverProps {
  outputValueRange: OutputValueRange[];
  outputValueRangeOptions: ValueRangeConfigOptions;
  updateOutputValueRange: (outputValueRange: OutputValueRange[]) => void;
  updateOutputValueRangeOptions: (outputValueRangeOptions: ValueRangeConfigOptions) => void;
  /**
   * Invoked once after both value updates on save. Kept separate from the setters so a single save
   * records one matrixOption history entry instead of one per updated field.
   */
  onCommit?: () => void;
}

export const ValueRangeConfigPopover = (
  {
    outputValueRange,
    outputValueRangeOptions,
    updateOutputValueRange,
    updateOutputValueRangeOptions,
    onCommit,
  }: ValueRangeConfigPopoverProps,
) => {
  const [
    selectedOptions, setSelectedOptions,
  ] = useState<ValueRangeConfigOptions>(outputValueRangeOptions);

  const handleOnChange = (
    key: keyof ValueRangeConfigOptions, option: ValueOf<ValueRangeConfigOptions>,
  ) => {
    setSelectedOptions({ ...selectedOptions, [key]: option });
  };

  const handleOnCancel = () => setSelectedOptions(outputValueRangeOptions);

  const handleOnSave = useCallback(() => {
    const updatedOutputValueRange = getUpdatedOutputValueRange({
      valueRangeConfigOptions: selectedOptions,
      outputValueRange,
    });

    updateOutputValueRange(updatedOutputValueRange);
    updateOutputValueRangeOptions(selectedOptions);
    onCommit?.();
  }, [
    outputValueRange,
    selectedOptions,
    updateOutputValueRange,
    updateOutputValueRangeOptions,
    onCommit,
  ]);

  return (
    <Box width="auto">
      <Popover
        trigger={(
          <Button
            variant={ButtonVariant.tertiary}
            icon={IconName.Settings}
          />
        )}
        align="center"
        side="right"
      >
        <Box direction="column" alignItems="flex-start" gap={5} style={{ padding: 'var(--cl-space-4)' }}>
          <Text lineHeight={4} as="h1" size={3} weight={500}>
            Configure Value Ranges
          </Text>
          <PopoverContent>
            <OptionGroup>
              High Range Setting
              <StyledBox>
                <StyledChip
                  onChange={() => handleOnChange('highRange', HighRangeOption.unbounded)}
                  selected={selectedOptions?.highRange === HighRangeOption.unbounded}
                  type="radio"
                  name={HighRangeOption.unbounded}
                  disabled={(
                    selectedOptions?.lowRange === LowRangeOption.unbounded
                    && outputValueRange.length < 2
                  )}
                >
                  Unbounded (<b>value</b> {' > '} X)
                </StyledChip>
                <StyledChip
                  onChange={() => handleOnChange('highRange', HighRangeOption.bounded)}
                  selected={selectedOptions?.highRange === HighRangeOption.bounded}
                  type="radio"
                  name={HighRangeOption.bounded}
                >
                  Bounded (<b>value</b> {' <= X < '} <b>value</b>)
                </StyledChip>
              </StyledBox>
            </OptionGroup>
            <OptionGroup>
              Low Range Setting
              <StyledBox>
                <StyledChip
                  onChange={() => handleOnChange('lowRange', LowRangeOption.unbounded)}
                  selected={selectedOptions?.lowRange === LowRangeOption.unbounded}
                  type="radio"
                  name={LowRangeOption.unbounded}
                  disabled={(
                    selectedOptions?.highRange === HighRangeOption.unbounded
                    && outputValueRange.length < 2
                  )}
                >
                  Unbounded (<b>value</b> {' <= '} X)
                </StyledChip>
                <StyledChip
                  onChange={() => handleOnChange('lowRange', LowRangeOption.bounded)}
                  selected={selectedOptions?.lowRange === LowRangeOption.bounded}
                  type="radio"
                  name={LowRangeOption.bounded}
                >
                  Bounded (<b>value</b> {' <= X < '} <b>value</b>)
                </StyledChip>
              </StyledBox>
            </OptionGroup>
          </PopoverContent>
          <PopoverFooter>
            <Popover.Close asChild>
              <Button onClick={handleOnCancel} data-dd-action-name="matrix-axis-config-cancel">
                Cancel
              </Button>
            </Popover.Close>
            <Popover.Close asChild>
              <Button
                data-dd-action-name="matrix-axis-config-save"
                onClick={handleOnSave}
                variant={ButtonVariant.primary}
              >
                Save
              </Button>
            </Popover.Close>
          </PopoverFooter>
          <Popover.Close asChild>
            <ModalCloseButton
              data-dd-action-name="matrix-axis-config-cancel"
              onClick={handleOnCancel}
              size={ButtonSize.small}
            />
          </Popover.Close>
        </Box>
      </Popover>
    </Box>
  );
};
